import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { ArrowRight, Car, Calendar, Users, Play } from 'lucide-react';
import { motion } from 'framer-motion';

export const Hero = () => {
  const { t } = useLanguage();

  const fadeUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: 'easeOut' }
  };

  return (
    <section 
      className="relative min-h-[85vh] overflow-hidden bg-gradient-to-b from-slate-50 to-white pt-20"
      data-testid="hero-section"
    >
      {/* Subtle grid background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
      
      {/* Floating geometric shapes */}
      <div className="absolute top-32 left-10 w-20 h-20 bg-blue-100/50 rounded-2xl rotate-12 animate-float"></div>
      <div className="absolute top-48 right-20 w-16 h-16 bg-slate-100 rounded-full animate-float-delayed"></div>
      <div className="absolute bottom-48 left-32 w-12 h-12 bg-blue-50 rounded-lg -rotate-12 animate-float-slow"></div>
      <div className="absolute top-64 right-1/4 w-8 h-8 bg-slate-200/50 rounded-full animate-float-delayed"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="flex flex-col items-center text-center max-w-5xl mx-auto">
          {/* Badge */}
          <motion.div 
            {...fadeUp}
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 rounded-full text-slate-600 text-sm font-medium mb-8"
          >
            <Car className="w-4 h-4" />
            <span>{t('hero_badge')}</span>
          </motion.div>

          {/* Title */}
          <motion.h1 
            {...fadeUp}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-6 leading-tight tracking-tight"
            data-testid="hero-title"
          >
            {t('hero_title_1')}
            <br />
            <span className="text-blue-600">{t('hero_title_2')}</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            {...fadeUp}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="text-base md:text-lg text-slate-600 mb-10 max-w-2xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t('hero_subtitle')}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            {...fadeUp}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 mb-16"
          >
            <Button 
              size="lg"
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-md px-8 py-6 text-base font-medium transition-all hover:shadow-lg"
              data-testid="hero-cta-primary"
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {t('hero_cta_primary')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-md px-8 py-6 text-base font-medium"
              data-testid="hero-cta-secondary"
            >
              <Play className="mr-2 w-5 h-5" />
              {t('hero_cta_secondary')}
            </Button>
          </motion.div>

          {/* Dashboard Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="w-full max-w-4xl"
            data-testid="hero-dashboard"
          >
            <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl overflow-hidden">
              {/* Window header */}
              <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border-b border-slate-100">
                <div className="flex gap-2">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                </div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-slate-400 font-medium">app.logirent.ch</span>
                </div>
              </div>
              
              {/* Dashboard content */}
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">L</span>
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900 text-sm">Logirent Dashboard</div>
                      <div className="text-xs text-slate-500">Vue d'ensemble</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="px-3 py-1.5 bg-slate-100 rounded-md text-xs text-slate-600">Aujourd'hui</div>
                  </div>
                </div>

                {/* Stats cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Car className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500">{t('stats_vehicles')}</span>
                    </div>
                    <div className="text-2xl font-bold text-slate-900">24</div>
                  </div>
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span className="text-xs text-blue-600">{t('stats_reservations')}</span>
                    </div>
                    <div className="text-2xl font-bold text-blue-600">156</div>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="w-4 h-4 text-slate-400" />
                      <span className="text-xs text-slate-500">{t('stats_clients')}</span>
                    </div>
                    <div className="text-2xl font-bold text-slate-900">89</div>
                  </div>
                </div>

                {/* Vehicle list preview */}
                <div className="space-y-2">
                  {['Tesla Model 3', 'BMW X5', 'Mercedes Classe A'].map((car, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white rounded-lg border border-slate-200 flex items-center justify-center">
                          <Car className="w-4 h-4 text-slate-500" />
                        </div>
                        <span className="text-slate-700 font-medium text-sm">{car}</span>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                        i === 0 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-slate-200 text-slate-600'
                      }`}>
                        {i === 0 ? 'Disponible' : 'En location'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* CSS for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(12deg); }
          50% { transform: translateY(-20px) rotate(12deg); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0) rotate(-12deg); }
          50% { transform: translateY(-10px) rotate(-12deg); }
        }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 5s ease-in-out infinite 1s; }
        .animate-float-slow { animation: float-slow 7s ease-in-out infinite 0.5s; }
      `}</style>
    </section>
  );
};
